import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Product, Recipe, RecipeIngredient, Ingredient, IngredientPrice } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { isAbsoluteRecipe } from "../../../utils/recipeQuantity";
import { t, dateLocale, compareLocale } from "../../../i18n/locale";

interface ProductFinancials {
  product_id: number;
  product_name: string;
  unit_weight_g: number;
  dough_cost_per_kg: number;
  ingredient_cost_per_piece: number;
  packaging_cost: number;
  total_cost_per_piece: number;
  selling_price: number;
  profit_per_piece: number;
  margin_pct: number;
  recipe?: Recipe;
}

/**
 * Product Costs Page - Calculate and display product costs
 */
const ProductCosts = () => {
  savePathToLocalStorage();

  const [products, setProducts] = useState<Product[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [financials, setFinancials] = useState<ProductFinancials[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ProductFinancials | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [onlyMissingPrice, setOnlyMissingPrice] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsData, recipesData, ingredientsData] = await Promise.all([
        mesApi.getProducts({ is_active: true }),
        mesApi.getRecipes({ is_active: true }),
        mesApi.getIngredients({ is_active: true }),
      ]);
      setProducts(productsData);
      setRecipes(recipesData);
      setIngredients(ingredientsData);
      calculateFinancials(productsData, recipesData, ingredientsData);
    } catch (err: any) {
      toast.error(t("Greska pri ucitavanju: {message}", { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const calculateFinancials = (
    products: Product[],
    recipes: Recipe[],
    ingredients: Ingredient[]
  ) => {
    const results: ProductFinancials[] = [];

    products.forEach((product) => {
      const activeRecipe = recipes.find(
        (r) => r.Product === product.ID && r.IsActive
      );

      let doughCostPerKg = 0;
      let totalPek = 0;

      if (activeRecipe?.RecipeIngredient_RecipeToRecipeIngredient_Recipe) {
        activeRecipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe.forEach((ri) => {
          const ingredient = ingredients.find(
            (i) => i.ID === ri.Ingredient
          );
          const currentPrice = getCurrentPrice(ingredient);
          const pek = ri.BakerPercentage || 0;
          totalPek += pek;

          if (currentPrice) {
            // Cost contribution = (pek% / 100) * price_per_kg
            doughCostPerKg += (pek / 100) * currentPrice;
          }
        });
      }

      // Normalize to actual dough cost per kg
      if (totalPek > 0) {
        doughCostPerKg = doughCostPerKg / (totalPek / 100);
      }

      const unitWeightKg = (product.UnitWeightG || 0) / 1000;
      const ingredientCostPerPiece = doughCostPerKg * unitWeightKg;
      const packagingCost = 0; // Would come from product settings
      const totalCostPerPiece = ingredientCostPerPiece + packagingCost;
      const sellingPrice = 0; // Would come from product pricing
      const profitPerPiece = sellingPrice - totalCostPerPiece;
      const marginPct = sellingPrice > 0 ? (profitPerPiece / sellingPrice) * 100 : 0;

      results.push({
        product_id: product.ID,
        product_name: product.ProductName,
        unit_weight_g: product.UnitWeightG || 0,
        dough_cost_per_kg: doughCostPerKg,
        ingredient_cost_per_piece: ingredientCostPerPiece,
        packaging_cost: packagingCost,
        total_cost_per_piece: totalCostPerPiece,
        selling_price: sellingPrice,
        profit_per_piece: profitPerPiece,
        margin_pct: marginPct,
        recipe: activeRecipe,
      });
    });

    setFinancials(results.sort((a, b) => a.product_name.localeCompare(b.product_name, compareLocale())));
  };

  const getCurrentPrice = (ingredient?: Ingredient): number => {
    if (!ingredient?.IngredientPrice) return 0;
    const now = new Date();
    const currentPrice = ingredient.IngredientPrice.find((p) => {
      const from = new Date(p.ValidFrom || "");
      const to = p.ValidTo ? new Date(p.ValidTo) : null;
      return from <= now && (!to || to >= now);
    });
    return currentPrice?.PricePerKg || 0;
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString(dateLocale(), {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }) + " EUR";
  };

  const formatPercent = (value: number) => {
    return value.toLocaleString(dateLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + "%";
  };

  const filteredFinancials = financials.filter((f) => {
    const matchesSearch = f.product_name.toLowerCase().includes(search.toLowerCase());
    const matchesMissing = !onlyMissingPrice || f.selling_price === 0;
    return matchesSearch && matchesMissing;
  });

  const getRecipeDetails = (recipe?: Recipe) => {
    if (!recipe?.RecipeIngredient_RecipeToRecipeIngredient_Recipe) return [];
    return recipe.RecipeIngredient_RecipeToRecipeIngredient_Recipe
      .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
      .map((ri) => {
        const ingredient = ingredients.find((i) => i.ID === ri.Ingredient);
        const price = getCurrentPrice(ingredient);
        const costPer100Pek = (ri.BakerPercentage || 0) * price / 100;
        return {
          ingredient_name: ingredient?.IngredientName || t("Nepoznato"),
          ingredient_type: ingredient?.IngredientTypeName || "",
          baker_percentage: ri.BakerPercentage || 0,
          price_per_kg: price,
          cost_per_100pek: costPer100Pek,
        };
      });
  };

  return (
    <div className="mes-page">
      <Header title={t("MES - Troskovi proizvoda")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Filters */}
          <div className="mes-section">
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
              <div className="form-group" style={{ flex: 2 }}>
                <label>{t("Pretrazi proizvod")}</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("npr. multigrain, ciabatta...")}
                  className="input-classic"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <input
                    type="checkbox"
                    checked={onlyMissingPrice}
                    onChange={(e) => setOnlyMissingPrice(e.target.checked)}
                  />
                  {t("Samo bez prodajne cijene")}
                </label>
              </div>
            </div>
            <p style={{ marginTop: "0.5rem", color: "#adb3bd" }}>
              <strong>{filteredFinancials.length}</strong> {t("proizvoda")}
            </p>
          </div>

          {/* Products Table */}
          {loading ? (
            <div className="mes-section">
              <p>{t("Ucitavanje...")}</p>
            </div>
          ) : (
            <div className="mes-section">
              <h3>{t("Pregled troskova")}</h3>
              <div style={{ overflowX: "auto" }}>
                <table className="mes-table">
                  <thead>
                    <tr>
                      <th>{t("Proizvod")}</th>
                      <th>{t("Masa (g)")}</th>
                      <th>{t("EUR/kg tijesta")}</th>
                      <th>{t("Sirovine/kom")}</th>
                      <th>{t("Pakiranje/kom")}</th>
                      <th>{t("Ukupni trosak/kom")}</th>
                      <th>{t("Prod. cijena")}</th>
                      <th>{t("Profit/kom")}</th>
                      <th>{t("Marza %")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFinancials.map((f) => (
                      <tr
                        key={f.product_id}
                        onClick={() => setSelectedProduct(f)}
                        style={{ cursor: "pointer" }}
                        className={selectedProduct?.product_id === f.product_id ? "selected-row" : ""}
                      >
                        <td>{f.product_name}</td>
                        <td>{f.unit_weight_g || "-"}</td>
                        <td>{f.dough_cost_per_kg > 0 ? formatCurrency(f.dough_cost_per_kg) : "-"}</td>
                        <td>{f.ingredient_cost_per_piece > 0 ? formatCurrency(f.ingredient_cost_per_piece) : "-"}</td>
                        <td>{f.packaging_cost > 0 ? formatCurrency(f.packaging_cost) : "-"}</td>
                        <td>{f.total_cost_per_piece > 0 ? formatCurrency(f.total_cost_per_piece) : "-"}</td>
                        <td>{f.selling_price > 0 ? formatCurrency(f.selling_price) : "-"}</td>
                        <td style={{ color: f.profit_per_piece < 0 ? "#e74c3c" : "#27ae60" }}>
                          {f.selling_price > 0 ? formatCurrency(f.profit_per_piece) : "-"}
                        </td>
                        <td style={{ color: f.margin_pct < 0 ? "#e74c3c" : f.margin_pct < 15 ? "#f39c12" : "#27ae60" }}>
                          {f.selling_price > 0 ? formatPercent(f.margin_pct) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recipe Detail */}
          {selectedProduct && (
            <div className="mes-section">
              <h3>{t("Detalj recepture: {name}", { name: selectedProduct.product_name })}</h3>

              {/* Metrics */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
                <div className="mes-metric-card">
                  <div className="metric-label">{t("Sirovine/kom")}</div>
                  <div className="metric-value">{formatCurrency(selectedProduct.ingredient_cost_per_piece)}</div>
                </div>
                <div className="mes-metric-card">
                  <div className="metric-label">{t("Pakiranje/kom")}</div>
                  <div className="metric-value">{formatCurrency(selectedProduct.packaging_cost)}</div>
                </div>
                <div className="mes-metric-card">
                  <div className="metric-label">{t("Ukupni materijal/kom")}</div>
                  <div className="metric-value">{formatCurrency(selectedProduct.total_cost_per_piece)}</div>
                </div>
                <div className="mes-metric-card">
                  <div className="metric-label">{t("Prodajna cijena")}</div>
                  <div className="metric-value">
                    {selectedProduct.selling_price > 0 ? formatCurrency(selectedProduct.selling_price) : "-"}
                  </div>
                  {selectedProduct.selling_price > 0 && (
                    <div className="metric-delta" style={{ color: selectedProduct.margin_pct >= 0 ? "#27ae60" : "#e74c3c" }}>
                      {t("{pct} marza", { pct: formatPercent(selectedProduct.margin_pct) })}
                    </div>
                  )}
                </div>
              </div>

              {/* Recipe Ingredients */}
              {isAbsoluteRecipe(selectedProduct.recipe) ? (
                // Obracun ovdje mnozi PEK % s cijenom po kilogramu; slasticarska
                // receptura nema postotaka, pa bi ispao trosak nula. Bolje reci da
                // obracuna nema nego prikazati nulu kao podatak.
                <p className="empty-state">
                  {t("Receptura je u apsolutnim kolicinama (g / kom / list). Obracun troska za slasticarske recepture jos nije podrzan.")}
                </p>
              ) : selectedProduct.recipe ? (
                <>
                  <div className="mes-table-wrap">
                    <table className="mes-table">
                    <thead>
                      <tr>
                        <th>{t("Sastojak")}</th>
                        <th>{t("Tip")}</th>
                        <th>{t("PEK %")}</th>
                        <th>{t("EUR/kg")}</th>
                        <th>{t("Trosak")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getRecipeDetails(selectedProduct.recipe).map((detail, idx) => (
                        <tr key={idx}>
                          <td>{detail.ingredient_name}</td>
                          <td><span className="mixer-badge">{detail.ingredient_type}</span></td>
                          <td>{Number(detail.baker_percentage).toFixed(3)}</td>
                          <td>{detail.price_per_kg > 0 ? Number(detail.price_per_kg).toFixed(4) : "-"}</td>
                          <td>{detail.cost_per_100pek > 0 ? Number(detail.cost_per_100pek).toFixed(4) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  <p style={{ marginTop: "1rem", color: "#adb3bd" }}>
                    {t("Ukupno PEK%:")} <strong>
                      {getRecipeDetails(selectedProduct.recipe).reduce((sum, d) => sum + Number(d.baker_percentage || 0), 0).toFixed(3)}
                    </strong> |
                    {t("Trosak/kg tijesta:")} <strong>{formatCurrency(selectedProduct.dough_cost_per_kg)}</strong> |
                    {t("Masa komada:")} <strong>{selectedProduct.unit_weight_g}g</strong>
                  </p>
                </>
              ) : (
                <p className="empty-state">{t("Nema aktivne recepture za ovaj proizvod.")}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default ProductCosts;
